import { useEffect, useMemo, useState } from "react";

import {
  addDays,
  addWeeks,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfDay,
  format,
  isSameDay,
  isSameMonth,
  isSameWeek,
  startOfDay
} from "date-fns";
import { Calendar, CalendarDays, Clock, Maximize2, Minimize2 } from "lucide-react";

import { Badge } from "../ui/badge.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs.jsx";

const RowItem = ({
  row,
  position,
  timeUnits,
  viewMode,
  renderLabel,
  renderBar,
  itemStartField = "startDate",
  itemEndField = "dueDate"
}) => {
  const { item, level } = row;

  return (
    <div
      className={`flex transition-all duration-200 group relative ${
        row.isFirstInGroup ? "mt-6 border-t border-slate-200/60 dark:border-yale-blue-800/40" : ""
      } ${row.isLastInGroup ? "border-b border-slate-200/60 dark:border-yale-blue-800/40" : ""} ${
        row.className || (level === 0 ? "bg-slate-50/30 dark:bg-yale-blue-900/10 h-14" : "bg-white dark:bg-yale-blue-950/5 h-16")
      }`}
    >
      {/* Item Label Container */}
      <div
        style={{ paddingLeft: `calc(${level} * (var(--indent, 12px)) + 24px)` }}
        className={`w-32 md:w-64 lg:w-96 flex-shrink-0 p-3 md:p-4 border-r border-slate-200/60 dark:border-yale-blue-800/40 sticky left-0 z-20 shadow-[4px_0_12px_rgba(0,0,0,0.02)] flex flex-col justify-center ${
          row.labelClassName ||
          (level === 0
            ? "bg-slate-50/90 dark:bg-yale-blue-900/90 backdrop-blur-sm"
            : "bg-white/90 dark:bg-yale-blue-950/90 backdrop-blur-sm")
        }`}
      >
        {/* Modern Tree Connectivity */}
        {level > 0 && (
          <>
            {/* Vertical connector line */}
            <div
              className={`absolute left-[16px] top-0 ${row.isLastInGroup ? "h-1/2" : "h-full"} w-px bg-slate-300 dark:bg-yale-blue-700`}
            />
            {/* Horizontal elbow line */}
            <div className="absolute left-[16px] top-1/2 w-4 h-px bg-slate-300 dark:bg-yale-blue-700" />
          </>
        )}

        {/* Minimalist Color Indicator */}
        <div
          className={`absolute left-0 top-0 bottom-0 w-1 ${row.groupColor?.replace("border-", "bg-") || "bg-transparent"} opacity-30`}
        />

        {renderLabel ? (
          renderLabel(row)
        ) : (
          <span
            className={`truncate text-yale-blue-800 dark:text-slate-100 ${
              level === 0 ? "text-sm font-extrabold tracking-tight" : "text-sm font-medium"
            }`}
          >
            {item.name || item.title || "Untitled"}
          </span>
        )}
      </div>

      {/* Timeline Visual Container */}
      <div
        className={`relative flex items-center h-full flex-1 transition-all duration-300 ${
          level > 0 ? "bg-yale-blue-50/[0.02] dark:bg-yale-blue-900/[0.02]" : ""
        }`}
        style={{
          width: viewMode?.toUpperCase() === "TODAY" ? "100%" : `${timeUnits.length * 64}px`,
          minWidth: viewMode?.toUpperCase() === "TODAY" ? "0" : "auto"
        }}
      >
        {/* Vertical Grid Lines & Duration Highlight */}
        <div className="flex flex-1 h-full relative overflow-hidden">
          {timeUnits.map((unit, i) => {
            const isWeekly = viewMode?.toUpperCase() === "WEEKLY";
            const unitStart = startOfDay(unit);
            const unitEnd = isWeekly ? endOfDay(addDays(unitStart, 6)) : endOfDay(unitStart);

            const startVal = item[itemStartField] || item.startDate || item.createdAt;
            const endVal = item[itemEndField] || item.dueDate || item.targetDate || startVal;

            const itemStart = startOfDay(new Date(startVal));
            const itemEnd = endOfDay(new Date(endVal));

            const isOverlapping = unitStart <= itemEnd && unitEnd >= itemStart;

            return (
              <div
                key={i}
                className={`flex-shrink-0 border-r border-yale-blue-50 dark:border-yale-blue-800/20 h-full transition-colors duration-300 ${
                  viewMode?.toUpperCase() === "TODAY" ? "flex-1" : "w-16"
                } ${isOverlapping ? "bg-yale-blue-400/5 dark:bg-yale-blue-400/5" : ""}`}
              />
            );
          })}
        </div>

        {/* Dynamic Item Bar */}
        {renderBar && renderBar(row, position)}
      </div>
    </div>
  );
};

const GanttChart = ({
  data,
  viewMode,
  title = "Timeline",
  subtitle,
  renderLabel,
  renderBar,
  itemStartField = "startDate",
  itemEndField = "dueDate",
  isDashboard = false,
  headerExtra,
  onViewModeChange
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isFullScreen]);

  const timeUnits = useMemo(() => {
    const isWeekly = viewMode?.toUpperCase() === "WEEKLY";
    const isTodayMode = viewMode?.toUpperCase() === "TODAY";

    if (isTodayMode) {
      const today = startOfDay(new Date());
      return [today];
    }

    const start = startOfDay(addDays(new Date(), -2));
    const end = addWeeks(start, 24);

    if (isWeekly) {
      return eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });
    }

    return eachDayOfInterval({ start, end });
  }, [viewMode]);

  const getItemPosition = (item) => {
    const startVal = item[itemStartField] || item.startDate || item.createdAt;
    const endVal = item[itemEndField] || item.dueDate || item.targetDate || startVal;

    const start = new Date(startVal);
    const end = new Date(endVal);

    if (isNaN(start) || isNaN(end)) return { left: 0, width: 0 };

    const isWeekly = viewMode?.toUpperCase() === "WEEKLY";
    const isTodayMode = viewMode?.toUpperCase() === "TODAY";

    if (isTodayMode) {
      return { left: 0, width: "100%" };
    }

    const startIndex = timeUnits.findIndex((unit) =>
      isWeekly ? isSameWeek(unit, start, { weekStartsOn: 1 }) : isSameDay(unit, start)
    );
    const endIndex = timeUnits.findIndex((unit) =>
      isWeekly ? isSameWeek(unit, end, { weekStartsOn: 1 }) : isSameDay(unit, end)
    );

    const left = startIndex !== -1 ? startIndex * 64 : 0;
    const width = Math.max(64, endIndex !== -1 ? (endIndex - startIndex + 1) * 64 : 64);

    return { left, width };
  };

  const Header = (
    <div
      className={`bg-white/95 dark:bg-yale-blue-950/95 backdrop-blur-md border-b border-slate-200 dark:border-yale-blue-800 ${viewMode?.toUpperCase() === "TODAY" ? "w-full" : "min-w-fit"}`}
    >
      {/* Month Row */}
      <div className="flex border-b border-slate-100 dark:border-yale-blue-800/50">
        <div className="w-32 md:w-64 lg:w-96 flex-shrink-0 border-r border-slate-200 dark:border-yale-blue-800 sticky left-0 z-50 bg-slate-100/80 dark:bg-yale-blue-900 backdrop-blur-sm" />
        <div className="flex flex-1">
          {timeUnits.map((unit, i) => {
            const isNewMonth = i === 0 || !isSameMonth(unit, timeUnits[i - 1]);
            if (!isNewMonth) return null;

            let span = 0;
            for (let j = i; j < timeUnits.length; j++) {
              if (isSameMonth(unit, timeUnits[j])) span++;
              else break;
            }

            return (
              <div
                key={`month-${i}`}
                className="h-8 flex items-center px-4 border-r border-slate-100 dark:border-yale-blue-800/50 flex-shrink-0 bg-yale-blue-50/50 dark:bg-yale-blue-900/20"
                style={{
                  width: viewMode?.toUpperCase() === "TODAY" ? "100%" : `${span * 64}px`
                }}
              >
                <span className="text-[10px] font-black text-yale-blue-600 dark:text-yale-blue-400 uppercase tracking-widest whitespace-nowrap">
                  {format(unit, "MMMM yyyy")}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day/Date Row */}
      <div className="flex">
        <div className="w-32 md:w-64 lg:w-96 flex-shrink-0 p-4 border-r border-slate-200 dark:border-yale-blue-800 font-bold text-yale-blue-500 dark:text-slate-400 text-[10px] uppercase tracking-[0.2em] flex items-center bg-slate-100/80 dark:bg-yale-blue-900 sticky left-0 z-50 backdrop-blur-sm">
          <span className="hidden md:inline">Schedule Details</span>
          <span className="md:hidden">Details</span>
        </div>
        <div className="flex flex-1">
          {timeUnits.map((unit, i) => {
            const isToday = isSameDay(unit, new Date());
            return (
              <div
                key={`day-${i}`}
                className={`h-12 flex-shrink-0 border-r border-slate-100 dark:border-yale-blue-800/50 flex flex-col items-center justify-center relative ${
                  isToday ? "bg-yale-blue-50/30 dark:bg-amber-900/10" : ""
                }`}
                style={{
                  width: viewMode?.toUpperCase() === "TODAY" ? "100%" : "64px"
                }}
              >
                <span
                  className={`text-xs font-bold ${isToday ? "text-yale-blue-600 dark:text-yale-blue-400" : "text-slate-600 dark:text-slate-400"}`}
                >
                  {viewMode?.toUpperCase() === "WEEKLY" ? `W${format(unit, "w")}` : format(unit, "d")}
                </span>
                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-tighter mt-0.5">
                  {viewMode?.toUpperCase() === "WEEKLY"
                    ? `${format(unit, "MMM d")}-${format(addDays(unit, 6), "d")}`
                    : format(unit, "EEE")}
                </span>
                {isToday && (
                  <div className="absolute bottom-0 w-full h-1 bg-primary rounded-t-full shadow-[0_-2px_8px_rgba(245,158,11,0.4)]" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <Card
      className={`overflow-hidden border-slate-200 dark:border-yale-blue-800 shadow-xl rounded-2xl bg-white dark:bg-yale-blue-950 transition-all duration-300 ${
        isFullScreen ? "fixed inset-0 z-[9999] rounded-none m-0 w-screen h-screen" : "relative"
      }`}
    >
      <CardHeader className="bg-yale-blue-50/50 dark:bg-yale-blue-900/30 border-b border-slate-200 dark:border-yale-blue-800 py-6 px-8">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold text-yale-blue-800 dark:text-slate-100 tracking-tight">{title}</CardTitle>
            {subtitle ? <p className="text-xs text-yale-blue-500 dark:text-slate-400 mt-1 font-medium">{subtitle}</p> : null}
          </div>
          <div className="flex items-center gap-3">
            {headerExtra}
            {onViewModeChange ? (
              <Tabs value={viewMode?.toUpperCase()} onValueChange={onViewModeChange} className="hidden md:block w-[300px]">
                <TabsList className="grid w-full grid-cols-3 rounded-xl bg-slate-100/50 dark:bg-yale-blue-800/50 p-1">
                  <TabsTrigger value="TODAY" className="text-xs rounded-lg py-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Today</span>
                  </TabsTrigger>
                  <TabsTrigger value="DAILY" className="text-xs rounded-lg py-2 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>Daily</span>
                  </TabsTrigger>
                  <TabsTrigger value="WEEKLY" className="text-xs rounded-lg py-2 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    <span>Weekly</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            ) : (
              <Badge
                variant="secondary"
                className="bg-white dark:bg-yale-blue-800 text-[10px] font-bold uppercase tracking-widest px-3 py-1 shadow-sm border-slate-200 dark:border-yale-blue-700"
              >
                {viewMode?.toUpperCase() === "TODAY" ? "Today" : viewMode?.toUpperCase() === "WEEKLY" ? "Weekly" : "Daily"} View
              </Badge>
            )}
            <button
              onClick={() => {
                setIsFullScreen(!isFullScreen);
              }}
              className="p-2 hover:bg-white/50 dark:hover:bg-yale-blue-800/50 rounded-lg transition-colors text-yale-blue-600 dark:text-yale-blue-400"
              title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullScreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div
          className="overflow-auto custom-scrollbar relative"
          style={{
            height: isFullScreen ? "calc(100vh - 84px)" : "calc(100vh - 300px)"
          }}
        >
          {/* Sticky Header */}
          <div className="sticky top-0 z-50 bg-inherit shadow-sm">{Header}</div>

          {/* List of rows without virtualization */}
          <div className="relative w-full">
            {data.map((row, index) => {
              const position = getItemPosition(row.item);
              return (
                <div key={index} style={{ height: "56px" }}>
                  <RowItem
                    row={row}
                    position={position}
                    timeUnits={timeUnits}
                    viewMode={viewMode}
                    renderLabel={renderLabel}
                    renderBar={renderBar}
                    itemStartField={itemStartField}
                    itemEndField={itemEndField}
                  />
                </div>
              );
            })}
          </div>

          <div className="h-20" />
        </div>
      </CardContent>
    </Card>
  );
};

export default GanttChart;
